// client/src/pages/PTO/PhoneCoverage.tsx
// Weekly phone duty display showing who covers phones each day.

import React, { useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import type { PhoneDutyAssignment, PTOEmployee } from './ptoTypes';
import { weekDatesFor, getOfficeColor, formatTime12 } from './ptoUtils';
import CoverageEditModal from './CoverageEditModal';

// ─── Props ──────────────────────────────────────────────────────────────────

interface PhoneCoverageProps {
  phoneDuty: PhoneDutyAssignment[];
  employees: PTOEmployee[];
  currentWeek: Date;
  canEdit: boolean;
  onSavePhoneDuty?: (payload: {
    dutyDate: string;
    staffIndex: string;
    timeIn: string | null;
    timeOut: string | null;
  }) => Promise<void>;
  onDeletePhoneDuty?: (id: number) => Promise<void>;
}

/** Render a time window or "All day" fallback. */
function formatSlotTimes(slot: PhoneDutyAssignment): string {
  if (slot.timeIn && slot.timeOut) {
    return `${formatTime12(slot.timeIn)} – ${formatTime12(slot.timeOut)}`;
  }
  return 'All day';
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayHeader(iso: string, idx: number): string {
  const [, m, d] = iso.split('-');
  return `${DAY_LABELS[idx]} ${Number(m)}/${Number(d)}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

const PhoneCoverage: React.FC<PhoneCoverageProps> = ({
  phoneDuty,
  employees,
  currentWeek,
  canEdit,
  onSavePhoneDuty,
  onDeletePhoneDuty,
}) => {
  const weekDates = useMemo(() => weekDatesFor(currentWeek), [currentWeek]);
  const today = todayIso();
  const [editDate, setEditDate] = useState<string | null>(null);

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Build duty lookup: date -> sorted slots
  const dutyByDate = useMemo(() => {
    const m = new Map<string, PhoneDutyAssignment[]>();
    for (const pd of phoneDuty) {
      const list = m.get(pd.dutyDate) ?? [];
      list.push(pd);
      m.set(pd.dutyDate, list);
    }
    // Sort each day's slots by timeIn (then by employee sort order)
    for (const list of m.values()) {
      list.sort((a, b) => {
        const aTime = a.timeIn ?? '00:00';
        const bTime = b.timeIn ?? '00:00';
        if (aTime !== bTime) return aTime.localeCompare(bTime);
        const ea = empMap.get(a.staffIndex);
        const eb = empMap.get(b.staffIndex);
        return (ea?.sortOrder ?? 0) - (eb?.sortOrder ?? 0);
      });
    }
    return m;
  }, [phoneDuty, empMap]);

  const editSlots = useMemo(() => (editDate ? dutyByDate.get(editDate) ?? [] : []), [editDate, dutyByDate]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 8,
        }}
      >
        {weekDates.map((d, i) => {
          const isToday = d === today;
          const slots = dutyByDate.get(d) ?? [];
          return (
            <div
              key={d}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: 8,
                borderRadius: 8,
                background: isToday ? 'rgba(122,162,255,.06)' : 'rgba(255,255,255,.02)',
                border: isToday ? '1px solid rgba(122,162,255,.2)' : '1px solid transparent',
                minHeight: 110,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isToday ? 'var(--accent)' : 'var(--muted)',
                  }}
                >
                  {dayHeader(d, i)}
                </div>
                {canEdit && (
                  <button
                    title="Edit phone coverage"
                    onClick={() => setEditDate(d)}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: 4, borderRadius: 4,
                      color: 'var(--muted)',
                    }}
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>

              {slots.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>
                  No coverage
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {slots.map((slot) => {
                    const emp = empMap.get(slot.staffIndex);
                    const officeColor = emp ? getOfficeColor(emp.office) : '#8090a0';
                    return (
                      <div
                        key={slot.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '4px 6px',
                          borderRadius: 5,
                          background: 'rgba(255,255,255,.03)',
                          borderLeft: `2px solid ${officeColor}`,
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                          {emp?.shortName || emp?.displayName || slot.staffIndex}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {formatSlotTimes(slot)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editDate && canEdit && onSavePhoneDuty && onDeletePhoneDuty && (
        <CoverageEditModal
          title="Phone Coverage"
          dateIso={editDate}
          slots={editSlots.map((s) => ({
            id: s.id, staffIndex: s.staffIndex, timeIn: s.timeIn, timeOut: s.timeOut,
          }))}
          allowOverlap
          employees={employees}
          onAdd={(form) =>
            onSavePhoneDuty({
              dutyDate: editDate,
              staffIndex: form.staffIndex,
              timeIn: form.timeIn,
              timeOut: form.timeOut,
            })
          }
          onEdit={(_id, form) =>
            onSavePhoneDuty({
              dutyDate: editDate,
              staffIndex: form.staffIndex,
              timeIn: form.timeIn,
              timeOut: form.timeOut,
            })
          }
          onDelete={onDeletePhoneDuty}
          onClose={() => setEditDate(null)}
        />
      )}
    </div>
  );
};

export default PhoneCoverage;
